/* eslint-disable no-param-reassign */

import { fromJS } from 'immutable';
import ConditionalityGraph from 'utils/conditionality.graph';
import { FIELD_NAMES, HOST_SET_ROUTE, LEGACY_GDPR_SETTINGS } from 'utils/constants';
import { getAnswersForField } from 'utils/branchingUtils';
import { makeDirty } from './ops.ui';

export function setThankYouRoute(state, fieldId, answer, url) {
  const thankYouKeyPath = ['form', 'thankYouRoutingFields', fieldId];
  const existingFieldRoutes = state.getIn(thankYouKeyPath).toJS();
  existingFieldRoutes[answer] = url;

  return state.setIn(thankYouKeyPath, fromJS(existingFieldRoutes));
}

export function refreshThankYouRoute(state, fieldId) {
  const thankYouKeyPath = ['form', 'thankYouRoutingFields', fieldId];
  const existingFieldRoutesRaw = state.getIn(thankYouKeyPath);

  if (existingFieldRoutesRaw) {
    const existingFieldRoutes = existingFieldRoutesRaw.toJS();
    const field = state.getIn(['form', 'fields', fieldId]).toJS();

    Object.values(field.editors.dropdownConfig.options).forEach((opt) => {
      if (opt.shouldDisplay && existingFieldRoutes[opt.value] === undefined) {
        existingFieldRoutes[opt.value] = HOST_SET_ROUTE;
      }

      if (!opt.shouldDisplay && existingFieldRoutes[opt.value] !== undefined) {
        delete existingFieldRoutes[opt.value];
      }
    });

    return state.setIn(thankYouKeyPath, fromJS(existingFieldRoutes));
  }

  return state;
}

export function toggleThankYouRoute(state, fieldId) {
  const thankYouKeyPath = ['form', 'thankYouRoutingFields', fieldId];
  const existingFieldRoutes = state.getIn(thankYouKeyPath);

  if (!existingFieldRoutes) {
    // get the field from the state
    const field = state.getIn(['form', 'fields', fieldId]).toJS();

    const newState = {};

    // get all possible answers from the dropdown of the field
    Object.values(field.editors.dropdownConfig.options).forEach((opt) => {
      if (opt.shouldDisplay) {
        newState[opt.value] = HOST_SET_ROUTE;
      }
    });

    return state.setIn(thankYouKeyPath, fromJS(newState));
  }

  return state.setIn(thankYouKeyPath, fromJS(undefined));
}

export function toggleBranching(state, fieldId) {
  const conditionalityInfoKeyPath = ['form', 'fieldConditionalityInfo', 'branchingFields', fieldId];
  const existingFieldBranches = state.getIn(conditionalityInfoKeyPath);
  if (!existingFieldBranches) {
    const field = state.getIn(['form', 'fields', fieldId]).toJS();
    const answers = getAnswersForField(field);
    const graph = new ConditionalityGraph(state.getIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph']).toJS());
    graph.setUserBranches(fieldId, answers);
    answers.forEach((answer) => graph.findOrCreateVertex({ left: fieldId, op: 'EQ', right: answer }));
    return state.setIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph'], fromJS(graph.dehydrate())).setIn(conditionalityInfoKeyPath, fromJS(answers));
  }
  const graph = new ConditionalityGraph(state.getIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph']).toJS());
  graph.unsetUserBranches(fieldId);
  return makeDirty(state.setIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph'], fromJS(graph.dehydrate())).deleteIn(conditionalityInfoKeyPath));
}

export function toggleBranchMembership(state, branchMemberId, answerValue, branchFieldId) { // eslint-disable-line
  const dehydrated = state.getIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph']).toJS();
  const graph = new ConditionalityGraph(dehydrated);
  const answerVertex = graph.getVertexForUserBranchingAnswer(branchFieldId, answerValue);
  const targetFieldVertex = graph.findOrCreateVertex({ left: branchMemberId, right: null, op: null });
  if (graph.getNearestBranchVerticesForFieldVertex(targetFieldVertex, branchFieldId).includes(answerValue)) {
    graph.removeAdjacentVertexFromPaths(targetFieldVertex, answerVertex);
  } else {
    graph.insertUserBranchingVertexAsAdjacentVertex(targetFieldVertex, answerVertex, branchFieldId);
  }
  return makeDirty(state.setIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph'], fromJS(graph.dehydrate())));
}

export function removeAllUserBranchMembership(state, fieldId) {
  const dehydrated = state.getIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph']);
  const userBranches = dehydrated.getIn(['userBranches']);
  const graph = new ConditionalityGraph(dehydrated.toJS());
  const fieldVertexIndex = graph.findOrCreateVertex({ left: fieldId });
  const answerVerticies = userBranches.valueSeq().flatMap((v) => v.toList());
  answerVerticies.forEach((answerVertex) => graph.removeAdjacentVertexFromPaths(fieldVertexIndex, answerVertex));

  return makeDirty(state.setIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph'], fromJS(graph.dehydrate())));
}

const FORM_LEVEL_GDPR = 'formLevelGDPR';
const FORM_LEVEL_NON_GDPR = 'formLevelNonGDPR';
const PHONE_VERTEX = 'phoneVertex';

// this returns an object with created or found indexes
function getIndexForFormLevelVerticies(graph, verticies) {
  return Object.keys(verticies).reduce((memo, key) => {
    switch (key) {
      case FORM_LEVEL_GDPR:
        memo[FORM_LEVEL_GDPR] = [
          graph.findOrCreateVertex({ left: 'state.no_klondike_gdpr_setting', op: 'EQ', right: 'always' }),
          graph.findOrCreateVertex({ left: 'state.no_klondike_gdpr_setting', op: 'EQ', right: 'geo' }),
          graph.findOrCreateVertex({ left: 'state.no_klondike_carmen_sandiego_region', op: 'EQ', right: 'eu' }),
        ];
        break;
      case FORM_LEVEL_NON_GDPR:
        memo[FORM_LEVEL_NON_GDPR] = [
          graph.findOrCreateVertex({ left: 'state.no_klondike_gdpr_setting', op: 'EQ', right: 'never' }),
          graph.findOrCreateVertex({ left: 'state.no_klondike_gdpr_setting', op: 'EQ', right: 'geo' }),
          graph.findOrCreateVertex({ left: 'state.no_klondike_carmen_sandiego_region', op: 'NE', right: 'eu' }),
        ];
        break;
      case PHONE_VERTEX:
        memo[PHONE_VERTEX] = [
          graph.replaceOrInsertVertex({ left: verticies[key] }, { action: 'optional if' }, false),
        ];
        break;
      default:
        break;
    }
    return memo;
  }, {});
}

function findVertexIndexByFieldId(graph, fieldId) {
  return graph.findOrCreateVertex({ left: fieldId });
}

export function removeLegacyConditionFromPhone(fieldId, graph) {
  // We are replacing the "optional if" action and removing the edges to condtional fields since the field is no longer phone (true means we will also orphan the node)
  graph.replaceOrInsertVertex({ left: fieldId }, { action: 'render if' }, true);
}

export function addLegacyConditionToPhone(fieldId, graph) {
  const vertexIndicies = getIndexForFormLevelVerticies(graph, { [FORM_LEVEL_GDPR]: true, [PHONE_VERTEX]: fieldId });
  const fieldVertexIndex = vertexIndicies[PHONE_VERTEX][0];
  graph.orphanFieldVertex(fieldVertexIndex); // We're only able to orphan because we do not allow phone field to be on a branch
  const [settingAlwaysIdx, settingGeoIdx, csdEURegionIdx] = vertexIndicies[FORM_LEVEL_GDPR];

  graph.addPath([0, settingAlwaysIdx, fieldVertexIndex], fieldVertexIndex);
  graph.addPath([0, settingGeoIdx, csdEURegionIdx, fieldVertexIndex], fieldVertexIndex);
}

// this function fires when a field name dropdown value changes. This can occur when adding a new field, or changing an existing field's dropdown
export function updateFieldSpecificConditions(state, keyPath, newFieldName, previousFieldName) {
  const graph = new ConditionalityGraph(state.getIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph']).toJS());
  const fieldId = state.getIn(['form', ...keyPath, 'id']);
  const gdprSetting = state.getIn(['form', 'syntheticContentEntities', 'gdprSetting']);
  const gdprSettingIsLegacy = LEGACY_GDPR_SETTINGS.includes(gdprSetting);
  // Remove any conditional data specific to the previous field type
  if (gdprSettingIsLegacy && previousFieldName === FIELD_NAMES.PHONE) {
    removeLegacyConditionFromPhone(fieldId, graph);
  } else if (previousFieldName === FIELD_NAMES.CONSENT_GDPR || previousFieldName === FIELD_NAMES.CONSENT_NON_GDPR) {
    const formLevelVerticies = previousFieldName === FIELD_NAMES.CONSENT_GDPR ? FORM_LEVEL_GDPR : FORM_LEVEL_NON_GDPR;
    const fieldVertexIndex = findVertexIndexByFieldId(graph, fieldId);
    const conditionalVerticiesToRemove = getIndexForFormLevelVerticies(graph, { [formLevelVerticies]: true })[formLevelVerticies];
    const updatedPaths = graph.getAllPathsFromVertex(fieldVertexIndex, fieldVertexIndex)
      .map((path) => path.filter((vertex) => !conditionalVerticiesToRemove.includes(vertex)));
    graph.orphanFieldVertex(fieldVertexIndex);
    updatedPaths.forEach((path) => graph.addPath(path, fieldVertexIndex));
  }

  // Add conditional data specific to the new field type
  if (gdprSettingIsLegacy && newFieldName === FIELD_NAMES.PHONE) {
    addLegacyConditionToPhone(fieldId, graph);
  } else if (newFieldName === FIELD_NAMES.CONSENT_GDPR) {
    // get/create the three relevant vertex indices for GDPR
    const vertexIndicies = getIndexForFormLevelVerticies(graph, { [FORM_LEVEL_GDPR]: true });

    // get/create the new gdpr consent vertex index itself
    const fieldVertexIndex = findVertexIndexByFieldId(graph, fieldId);

    // if it turns out gdpr vertex existed, but was orphaned, we need to connect it to root
    // the found orphan situation occurs because you had a field, then deleted it, without reloading the page. that node will just have no connections
    // this would be a problem if you re add the field, since the next steps expect it to at least be connected to the root
    if (graph.isVertexOrphaned(fieldVertexIndex)) {
      graph.addPath([0, fieldVertexIndex], fieldVertexIndex);
    }

    // now, we need to get all the paths to root, it'll likely only be one path [0,vertexIndex], in the paths array, so [[0,vertexIndex]]
    const pathsToRoot = graph.getAllPathsFromVertex(fieldVertexIndex, fieldVertexIndex);

    // now we INTENTIONALLY orphan it so we can rewrite the paths we need
    graph.orphanFieldVertex(fieldVertexIndex);

    pathsToRoot
      .map((pathToRoot) => pathToRoot.slice(1)) // we are slicing to remove the root index, which will always be first
      .forEach((path) => {
        const [settingAlwaysIdx, settingGeoIdx, csdEURegionIdx] = vertexIndicies[FORM_LEVEL_GDPR];
        // gdpr_vertex -> setting_always -> root
        graph.addPath([0, settingAlwaysIdx, ...path], fieldVertexIndex);
        // gdpr_vertex -> csd_eu -> setting_geo -> root
        graph.addPath([0, settingGeoIdx, csdEURegionIdx, ...path], fieldVertexIndex);
      });

  } else if (newFieldName === FIELD_NAMES.CONSENT_NON_GDPR) {
    const vertexIndicies = getIndexForFormLevelVerticies(graph, { [FORM_LEVEL_NON_GDPR]: true });
    const fieldVertexIndex = findVertexIndexByFieldId(graph, fieldId);
    if (graph.isVertexOrphaned(fieldVertexIndex)) {
      graph.addPath([0, fieldVertexIndex], fieldVertexIndex);
    }
    const pathsToRoot = graph.getAllPathsFromVertex(fieldVertexIndex, fieldVertexIndex);
    graph.orphanFieldVertex(fieldVertexIndex);
    pathsToRoot
      .map((pathToRoot) => pathToRoot.slice(1)) // we are slicing to remove the root index, which will always be first
      .forEach((path) => {
        const [settingNeverIdx, settingGeoIdx, csdNonEURegionIdx] = vertexIndicies[FORM_LEVEL_NON_GDPR];
        graph.addPath([0, settingNeverIdx, ...path], fieldVertexIndex);
        graph.addPath([0, settingGeoIdx, csdNonEURegionIdx, ...path], fieldVertexIndex);
      });
  }
  return state.setIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph'], fromJS(graph.dehydrate()));
}
